import type { Client, Project, Task } from '../types/crm';
import { activePortalPath } from '../lib/portal';
import { CRM_TIMEZONE, parseCrmDate } from './dateTime';
import { clientById } from './crmHelpers';
import { taskDisplayStatus } from './taskHelpers';

/** YYYY-MM-DD in Singapore time — for calendar keys and day filters. */
export function singaporeYmdKey(iso?: string | null): string | null {
  const d = parseCrmDate(iso);
  if (!d) return null;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CRM_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

export function singaporeCalendarParts(date: Date = new Date()) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: CRM_TIMEZONE,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    })
      .formatToParts(date)
      .map((p) => [p.type, p.value]),
  );
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
  };
}

/** Pending tasks: nearest due date first; no due date last. */
export function comparePendingTasksByDue(a: Task, b: Task): number {
  const da = parseCrmDate(a.due_date);
  const db = parseCrmDate(b.due_date);
  if (!da && !db) {
    return (a.title ?? '').localeCompare(b.title ?? '', undefined, { sensitivity: 'base' });
  }
  if (!da) return 1;
  if (!db) return -1;
  const diff = da.getTime() - db.getTime();
  if (diff !== 0) return diff;
  return (a.title ?? '').localeCompare(b.title ?? '', undefined, { sensitivity: 'base' });
}

export function filterOpenTasks(tasks: Task[]): Task[] {
  return tasks.filter((t) => (t.status ?? 'To Do') !== 'Done');
}

export function sortPendingTasks(tasks: Task[]): Task[] {
  return [...filterOpenTasks(tasks)].sort(comparePendingTasksByDue);
}

/** Dashboard task list: open tasks first (by due), then Done. */
export function sortDashboardTasks(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    const aDone = (a.status ?? 'To Do') === 'Done';
    const bDone = (b.status ?? 'To Do') === 'Done';
    if (aDone !== bDone) return aDone ? 1 : -1;
    return comparePendingTasksByDue(a, b);
  });
}

/** Per-day calendar dot: open if any incomplete; done if all complete. */
export function buildTaskCalendarDayDots(
  tasks: Task[],
): Map<string, 'open' | 'done'> {
  const counts = new Map<string, { open: number; done: number }>();
  for (const task of tasks) {
    const key = singaporeYmdKey(task.due_date);
    if (!key) continue;
    const entry = counts.get(key) ?? { open: 0, done: 0 };
    if ((task.status ?? 'To Do') === 'Done') entry.done += 1;
    else entry.open += 1;
    counts.set(key, entry);
  }
  const dots = new Map<string, 'open' | 'done'>();
  for (const [key, entry] of counts) {
    dots.set(key, entry.open > 0 ? 'open' : 'done');
  }
  return dots;
}

export function countOverdueTasks(tasks: Task[]): number {
  return filterOpenTasks(tasks).filter((t) => taskDisplayStatus(t) === 'Due').length;
}

export function countDueWithinDays(tasks: Task[], days: number): number {
  const now = Date.now();
  const end = now + days * 24 * 60 * 60 * 1000;
  return filterOpenTasks(tasks).filter((t) => {
    const d = parseCrmDate(t.due_date);
    if (!d) return false;
    const ts = d.getTime();
    return ts >= now && ts <= end;
  }).length;
}

export function isActiveProject(project: Project): boolean {
  const status = (project.status ?? 'Ongoing').trim();
  return status.toLowerCase() !== 'completed';
}

export interface CompanyProjectGroup {
  clientId: string;
  companyName: string;
  companyCode: string;
  projects: Project[];
}

export function groupProjectsByCompany(projects: Project[], clients: Client[]): CompanyProjectGroup[] {
  const map = new Map<string, CompanyProjectGroup>();

  for (const project of projects) {
    if (!isActiveProject(project)) continue;

    const client = clientById(clients, project.client_id);
    const clientId = client?.id ?? '_unassigned';
    const companyName = client?.company_name?.trim() || 'No account linked';
    const companyCode = client?.code ?? '—';

    let group = map.get(clientId);
    if (!group) {
      group = { clientId, companyName, companyCode, projects: [] };
      map.set(clientId, group);
    }
    group.projects.push(project);
  }

  for (const group of map.values()) {
    group.projects.sort((a, b) => {
      const ref = (a.reference_no ?? '').localeCompare(b.reference_no ?? '', undefined, {
        sensitivity: 'base',
      });
      if (ref !== 0) return ref;
      return (a.title ?? '').localeCompare(b.title ?? '', undefined, { sensitivity: 'base' });
    });
  }

  return [...map.values()].sort((a, b) =>
    a.companyName.localeCompare(b.companyName, undefined, { sensitivity: 'base' }),
  );
}

export function projectDetailPath(project: Project): string {
  return project.reference_no
    ? activePortalPath(`projects/${encodeURIComponent(project.reference_no)}`)
    : activePortalPath(`projects/id/${project.id}`);
}

export function buildSingaporeMonthGrid(year: number, month: number) {
  const days: { ymd: string; day: number }[] = [];
  for (let d = 1; d <= 31; d++) {
    const iso = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}T12:00:00+08:00`;
    const key = singaporeYmdKey(iso);
    if (!key) continue;
    const [y, m] = key.split('-').map(Number);
    if (y === year && m === month) days.push({ ymd: key, day: d });
  }

  const firstIso = `${year}-${String(month).padStart(2, '0')}-01T12:00:00+08:00`;
  const firstDate = parseCrmDate(firstIso)!;
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone: CRM_TIMEZONE,
    weekday: 'short',
  }).format(firstDate);
  const startOffset = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(weekday);

  return { days, startOffset, year, month };
}
